import { useTranslation, untranslated } from "@i18n";
import React, { useEffect, useState, forwardRef, useMemo } from "react";
import IconComponent from "@CubeComponents/icons";
import { useResizeDetector } from "react-resize-detector";
import Button from "@mui/material/Button";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ToolTipComponent from "../tooltip";
import SortGroupModal from "./Modals/SortGroupModal";
import FilterModal from "./Modals/FilterModal";
import GridColumnChooser from "./GridColumnChooser";
import Text from "../Text/Text";
import RowResizeModal from "./RowResizeModal";
import { exportGroupedExcel } from ".";
import { TOOLBAR_PROPERTIES_ACTION } from "./toolbarUtils";
import _, { get } from "lodash";
import Views from "@Components/ViewsNewFlow";
import { getFormatterByType } from "./Formatters/FormattersRegistery";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import type { Column } from "slickgrid-react";
import type { TranslatedString } from "@i18n";
import type {
  AppliedFilterCondition,
  GridRowBase,
  GroupCondition,
  SlickGrid1Ref,
  SlickgridReactInstanceX,
  SortCondition,
  ToolbarItem,
  FilterCondition as GridFilterCondition,
} from ".";
import type { FilterModalProps } from "./Modals/FilterModal";
import type { SortGroupModalProps } from "./Modals/SortGroupModal";
import type { GridLike } from "./Formatters/Formatters";
import type { VirtualAnchorElement } from "./OptionDropDown";

/**
 * A toolbar entry as this component renders it. The exported `ToolbarItem` calls the label `text`
 * and does not declare the Syncfusion-era keys below, which ~6 call sites still pass by extending
 * `ToolbarItem` locally - so they are named here rather than widened on the public type.
 *
 * `name` is the button label and is forwarded verbatim to ButtonComponent, which hands it to MUI as
 * the DOM button's `name` attribute - so it is a string, not a node. `tooltip` is a plain string or
 * an element; the string is NOT guaranteed to be translated, see {@link toolbarTooltipContent}.
 */
type ToolBarEntry = ToolbarItem & {
  name?: string;
  tooltip?: string | React.ReactElement;
  hidden?: boolean;
  buttonType?: string;
  prefixIconTemplate?: React.ReactNode;
  suffixIconTemplate?: React.ReactNode;
};

/**
 * A grid column as this toolbar reads it. Every value annotated with it is a live slickgrid column -
 * read off the grid instance (`getColumns`) or off the grid service (`getAllColumnDefinitions`), or
 * handed to a formatter by slickgrid itself - so `id`/`field` are required exactly as `Column`
 * declares them, and the column chooser can hand the rebuilt list straight back to `setColumns`.
 * Kept structural rather than intersected with `Column<T>` because the extras below are SlickGrid1's
 * own additions, which slickgrid does not declare.
 */
type ToolBarColumn = {
  id: string | number;
  field: string;
  width?: number;
  formatter?: Column["formatter"];
  formatterType?: string;
  clipMode?: unknown;
  /** Nullable to match slickgrid's own `Column`, whose columns are read through this type. */
  headerCssClass?: string | null;
  excludeFromColumnPicker?: boolean;
  initiallyHidden?: boolean;
  _valueGetter?: (row: GridRowBase) => unknown;
};

/** Column definitions as the grid hands them to the toolbar and forwards to the filter/sort modals. */
type ToolBarColumnDefinition = {
  id?: string | number;
  /** `keyof TRow` on the grid side, so widened to PropertyKey rather than `string` here. */
  field?: PropertyKey;
  name?: string;
  filterType?: string;
  disableFiltering?: boolean;
  disableSorting?: boolean;
  disableGrouping?: boolean;
};

/** Props of `<Views>` as this toolbar forwards them. */
type ViewsProps = React.ComponentProps<typeof Views>;

/** The saved view `<Views>` hands back when one is picked - the server `LayoutData`. */
type SavedViewPayload = Parameters<NonNullable<ViewsProps["onClickLayout"]>>[0];

/**
 * The toolbar's props. Every entry is optional: SlickGrid1 supplies the whole set, but the Gantt
 * (`Schedule/Gantt/indexNew.tsx`) mounts the same component with only the toolbar + views props, so
 * the ones it omits are asserted non-null at their single use rather than defaulted — a default
 * would emit.
 *
 * Several members are `unknown` because the grid and the modal on the far side genuinely disagree
 * about their shape (response envelopes, predicate maps, the views payload). Each is asserted at the
 * one JSX site that forwards it, with the disagreement noted there.
 */
interface ToolBarProps {
  toolbar?: ToolBarEntry[];
  /** Method shorthand: SlickGrid1 hands this a handler declared `(cmd: string) => void`. */
  toolbarClickHandler?(id?: string): void;
  showToolbarBorder?: boolean;
  enableColumnChooser?: boolean;
  enableSearchBar?: boolean;
  searchPlaceholder?: string;
  searchTerm?: string;
  onSearchChange?(next: string): void;
  searchDebounceMs?: number;
  columnDefinitions?: ToolBarColumnDefinition[] | null;
  propertiesPanel?: boolean;
  propertiesDisabled?: boolean;
  height?: number | string;
  excelExportServiceRef?: React.MutableRefObject<boolean>;
  excelExportExtension?: { exportToExcel: () => void } | null;
  /** `column` stays `unknown`: the grid's own column type is neither sub- nor supertype of ours. */
  ellipseFormatterWithTooltip?(args: { value: unknown; column?: unknown }): HTMLElement;
  excelExportCallback?: () => void;
  excelExport?: boolean;
  excelExportDisabled?: boolean;
  enableFilteringOption?: boolean;
  enableSortingOption?: boolean;
  enableGroupingOption?: boolean;
  enableRefreshOption?: boolean;
  refreshOptionCallBack?: () => void;
  modalState?: string;
  setModalState?(state: string): void;
  stateRef?: React.MutableRefObject<{ enableWrapText?: boolean } | null>;
  /** The grid also anchors modals to a virtual (rect-only) element, e.g. a right-clicked cell. */
  modalAnchor?: HTMLElement | VirtualAnchorElement | null;
  sortConditions?: SortCondition[];
  setSortConditions?: React.Dispatch<React.SetStateAction<SortCondition[]>>;
  filterConditions?: AppliedFilterCondition[];
  setFilterConditions?: React.Dispatch<React.SetStateAction<AppliedFilterCondition[]>>;
  /** The grid's remote source: a URL, or the fetcher it wraps one in. */
  remoteDataSource?: unknown;
  callRemote?(source: string, body: { value: unknown }): Promise<unknown>;
  beforeDataBound?: unknown;
  datasetForFetchingOptions?: unknown[];
  query?: unknown;
  getUpdatedFilterConditions?: FilterModalProps["getUpdatedFilterConditions"];
  applyFilterFunction?: FilterModalProps["applyFilterFunction"];
  setAppliedFilters?: React.Dispatch<React.SetStateAction<string[]>>;
  appliedFilters?: string[];
  setFilterPredicates?: React.Dispatch<React.SetStateAction<Record<string, unknown> | string>>;
  setFilterConditionsObject?: React.Dispatch<React.SetStateAction<Record<string, GridFilterCondition>>>;
  groupConditions?: GroupCondition[];
  setGroupConditions?: React.Dispatch<React.SetStateAction<GroupCondition[]>>;
  enableRowResize?: boolean;
  handleRowResizeOptionClick?: (size: string, height: number) => void;
  currentColumnId?: string;
  /** `onClickLayout` is passed as its own top-level prop below instead — see the note on it. */
  viewsOptions?: Omit<ViewsProps, "onClickLayout">;
  enableViews?: boolean;
  onViewsLoaded?: ViewsProps["onViewsLoaded"];
  createNewLayoutData?: unknown;
  /**
   * The two mount sites signal "nothing to push" differently (`false` from SlickGrid1, `null` from
   * the Gantt) and carry their own view payload; <Views> declares this `object` and only ever tests
   * it for truthiness, so both empty signals are normalised at the call site below.
   */
  triggerUpdateCurrentView?: object | false | null;
  /**
   * Method shorthand on purpose (not an arrow property): <Views> declares this against the server
   * saved-view payload, while the two mount sites hand over a handler written against their own
   * (SlickGrid1's `LayoutData`, index.tsx:505; the Gantt's `GanttView`). A contravariant parameter
   * would reject both, and this toolbar only forwards the handler - it never calls it.
   */
  onClickLayout?(data: SavedViewPayload): void;
  setTempCondition?: React.Dispatch<React.SetStateAction<AppliedFilterCondition | null>>;
  infiniteScroll?: boolean;
  tempCondition?: AppliedFilterCondition | null;
  setEnableWrapText?: (value: boolean) => void;
  enableWrapText?: boolean;
  /** The grid's own pinned/system columns, spliced back in ahead of the chosen ones. */
  specialColumns?: Column[];
  onSaveColumns?(cols: unknown[]): void;
  gridId?: string;
  /** Forwarded by SlickGrid1 but not read here; declared so the JSX attribute is not excess. */
  sortDataset?: unknown;
  dataset?: unknown;
  filterType?: unknown;
  localData?: unknown;
  callbackForVisibleColumnsUpdate?: unknown;
}

/** Props of the debounced search box rendered inside the toolbar. */
interface GridSearchInputProps {
  value?: string;
  placeholder?: string;
  onSearchChange?: (next: string) => void;
  debounceMs?: number;
}

type RowSizeKey = "small" | "medium" | "large" | "wrap";

const GridSearchInput = ({ value = "", placeholder, onSearchChange, debounceMs = 300 }: GridSearchInputProps) => {
  const { t } = useTranslation();
  const [local, setLocal] = useState<string>(value);
  const [expanded, setExpanded] = useState<boolean>(Boolean(value));
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setLocal(value);
    if (value) setExpanded(true);
  }, [value]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const emit = (next: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSearchChange?.(next), debounceMs);
  };

  const handleExpand = () => {
    setExpanded(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleBlur = () => {
    if (!local) setExpanded(false);
  };

  if (!expanded) {
    return (
      <ToolTipComponent content={t("Search")}>
        <ButtonComponent
          className="me-2"
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          iconButton={true}
          onClick={handleExpand}
        >
          <IconComponent name="SearchIcon" width="1.5rem" height="1.5rem" />
        </ButtonComponent>
      </ToolTipComponent>
    );
  }

  return (
    <div className="flex items-center min-w-[10rem] max-w-[16rem] me-2">
      <TextFieldComponent
        id="gridSearchBox"
        ref={inputRef}
        value={local}
        placeholder={placeholder || t("Search")}
        onBlur={handleBlur}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const next = e.target.value;
          setLocal(next);
          emit(next);
        }}
        slotProps={{
          input: {
            // Show a clear (cross) icon while there is text, so the user can wipe the
            // search in one click; fall back to the search icon when the field is empty.
            endAdornment: local ? (
              <IconComponent
                name="cross"
                width="1.25rem"
                height="1.25rem"
                toolTipContent={t("Clear")}
                onClick={() => {
                  if (timerRef.current) clearTimeout(timerRef.current);
                  setLocal("");
                  onSearchChange?.("");
                  requestAnimationFrame(() => inputRef.current?.focus());
                }}
              />
            ) : (
              <IconComponent name="SearchIcon" width="1.25rem" height="1.25rem" />
            ),
          },
        }}
      />
    </div>
  );
};

/**
 * ToolTipComponent brands its `content` as a `TranslatedString`. A toolbar entry's tooltip is
 * whatever the caller put in the toolbar array: some pass a `t(...)` result, but bare English
 * literals are still live at 12 call sites - contracts/components/Contracts/index.tsx:151,157,
 * ProjectSetings/UserAdminstration/Roles/ListRoles/listRoles.tsx:387,394,401,409,
 * components/Schedule/ListSchedule/index.tsx:158,165,172 and
 * pages/TaskManagement/ListTask/listTask.tsx:237,249,256 - so this does NOT arrive translated.
 *
 * This component cannot close that gap: `t()` only accepts keys declared in en.json, and most of
 * those literals ("Create Main Contract", "Add new role", "Task Creation", ...) are not keys at
 * all, so a runtime lookup would hand back the same English. The shared `untranslated()` mint is
 * therefore used deliberately, to keep the boundary greppable - the fix belongs at those call
 * sites, which own the strings.
 */
const toolbarTooltipContent = (tooltip: string | React.ReactElement): TranslatedString | React.ReactElement =>
  typeof tooltip === "string" ? untranslated(tooltip) : tooltip;

const getRowIcon = (size: string) => {
  let iconName: string;
  if (size === "small") {
    iconName = "RowResizeIcon";
  } else if (size === "wrap") {
    iconName = "WrapText";
  } else if (size === "medium") {
    iconName = "MediumCell";
  } else {
    iconName = "LargeRowSize";
  }
  return <IconComponent name={iconName} width="1.5rem" height="1.5rem" />;
};

const ToolBar = forwardRef(
  (
    {
      toolbar = [],
      toolbarClickHandler,
      showToolbarBorder = false,
      enableColumnChooser = false,
      enableSearchBar = false,
      searchPlaceholder = "",
      searchTerm = "",
      onSearchChange,
      searchDebounceMs = 300,
      columnDefinitions = [],
      propertiesPanel,
      propertiesDisabled = false,
      height = 24,
      excelExportServiceRef,
      excelExportExtension,
      ellipseFormatterWithTooltip,
      excelExportCallback,
      excelExport,
      excelExportDisabled = false,
      enableFilteringOption = false,
      enableSortingOption = false,
      enableGroupingOption = false,
      enableRefreshOption = false,
      refreshOptionCallBack,
      modalState,
      setModalState,
      stateRef,
      modalAnchor,
      sortConditions,
      setSortConditions,
      filterConditions,
      setFilterConditions,
      remoteDataSource,
      callRemote,
      beforeDataBound,
      datasetForFetchingOptions,
      query,
      getUpdatedFilterConditions,
      applyFilterFunction,
      setAppliedFilters,
      appliedFilters,
      setFilterPredicates,
      setFilterConditionsObject,
      groupConditions,
      setGroupConditions,
      enableRowResize,
      handleRowResizeOptionClick,
      currentColumnId,
      viewsOptions,
      enableViews,
      onViewsLoaded,
      createNewLayoutData,
      triggerUpdateCurrentView,
      onClickLayout,
      setTempCondition,
      infiniteScroll,
      tempCondition,
      setEnableWrapText,
      enableWrapText,
      specialColumns,
      onSaveColumns,
      gridId,
    }: ToolBarProps,
    ref: React.ForwardedRef<unknown>,
  ) => {
    const { t } = useTranslation();
    const [showOverFlowBtns, setShowOverFlowBtns] = useState<boolean>(false);
    const { width, ref: toolRef } = useResizeDetector();
    /**
     * MODAL ANCHOR. `modalAnchor` is genuinely NOT an HTMLElement on the live path: the only value
     * SlickGrid1 ever stores in it is the rect-only object OptionDropDown builds for the clicked
     * column menu (OptionDropDown.tsx:297-313) - a MUI `VirtualElement`, which is what Popper
     * accepts (both modals hand it straight to `<Popper anchorEl>`: FilterModal.tsx:1733,
     * SortGroupModal.tsx:307). The mismatch is that FilterModal and SortGroupModal declare the slot
     * `HTMLElement | null` (FilterModal.tsx:108, SortGroupModal.tsx:53); widening those two
     * declarations to `HTMLElement | VirtualAnchorElement | null` is the real fix and is the only
     * one - narrowing here with an `instanceof` check would drop the anchor the grid actually uses.
     * Stated once instead of at each of the three modal call sites.
     */
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the modals' HTMLElement-only anchor declaration is the mismatch, see above; the fix belongs in FilterModal.tsx:108 / SortGroupModal.tsx:53
    const modalAnchorEl = modalAnchor as HTMLElement | null | undefined;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(modalAnchorEl ?? null);
    const [showPropertiesPanel, setShowPropertiesPanel] = useState<boolean>(false);
    const [showColumnChooser, setShowColumnChooser] = useState<boolean>(false);
    const [showRowResize, setShowRowResize] = useState<boolean>(false);
    const [currentRowSize, setCurrentRowSize] = useState<RowSizeKey>("small");
    /**
     * SlickGrid1 forwards the mounted grid instance here (the toolbar is not a DOM ref target); the
     * Gantt mounts the same component with no ref at all, in which case React passes `null`. The
     * component cannot be declared `forwardRef<SlickgridReactInstanceX<GridRowBase>>`: the grid's
     * extras take the row type in parameter position (`addRecord`, `removeRecord`), so they are
     * invariant in it and the grid's own `MutableRefObject<SlickgridReactInstanceX<TRow>>` would no
     * longer be assignable. A callback ref - or no ref - reads as an empty ref rather than as a
     * null dereference, which is what `gridRef` used to become on the Gantt.
     */
    const standByGridRef = React.useRef<SlickgridReactInstanceX<GridRowBase> | null>(null);
    const gridRef: React.RefObject<SlickgridReactInstanceX<GridRowBase> | null> =
      typeof ref === "object" && ref !== null
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- verified: typing this param `ForwardedRef<SlickgridReactInstanceX<GridRowBase>>` makes SlickGrid1's own `ref={gridRef}` fail (TS2322 at index.tsx:5461, `addRecord`'s row parameter)
          (ref as React.MutableRefObject<SlickgridReactInstanceX<GridRowBase> | null>)
        : standByGridRef;

    const toggleGrouping = (toggleType: string) => {
      if (toggleType === "expand") {
        gridRef.current?.dataView.expandAllGroups();
      } else {
        gridRef.current?.dataView.collapseAllGroups();
      }
    };
    useEffect(() => {
      if (toolRef.current) {
        const ele = toolRef.current;
        if (ele.scrollWidth - ele.scrollLeft === ele.clientWidth) {
          setShowOverFlowBtns(false);
        } else {
          setShowOverFlowBtns(true);
        }
      }
    }, [width, toolRef]);
    useEffect(() => {
      setAnchorEl(null);
    }, [modalAnchor]);
    const rightArray = useMemo(
      () => toolbar.filter((item: ToolBarEntry) => item.rightAllign && !item.sticky),
      [toolbar],
    );
    const leftArray = useMemo(
      () => toolbar.filter((item: ToolBarEntry) => !item.rightAllign && !item.sticky),
      [toolbar],
    );
    const rightStickyArray = useMemo(
      () => toolbar.filter((item: ToolBarEntry) => item.rightAllign && item.sticky),
      [toolbar],
    );
    const leftStickyArray = useMemo(
      () => toolbar.filter((item: ToolBarEntry) => !item.rightAllign && item.sticky),
      [toolbar],
    );

    const handleArrowClick = (direction: string) => {
      if (toolRef.current) {
        const scrollAmount = direction === "forward" ? 200 : -200;
        toolRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    };

    const viewsTemplate = () => {
      return (
        <div className="flex">
          <Views
            {...viewsOptions}
            onClickLayout={onClickLayout}
            createNewLayoutData={createNewLayoutData}
            triggerUpdateCurrentView={triggerUpdateCurrentView || undefined}
            onViewsLoaded={onViewsLoaded}
          />
          <div className="w-px h-8 bg-[#666A84] mx-1" />
        </div>
      );
    };

    const handleClick = (id: string | undefined) => {
      if (toolbarClickHandler) {
        toolbarClickHandler(id);
      }
    };

    const handleExportToExcel = () => {
      if (remoteDataSource && infiniteScroll) {
        excelExportServiceRef!.current = true;
        // Use the provided callback, or fall back to grid refresh which
        // loads ALL data (NO_OF_FETCHED_ROWS) and triggers the export
        // in the loadData data-bound handler.
        if (excelExportCallback) {
          excelExportCallback();
        } else {
          gridRef.current?.refresh!();
        }
      } else {
        if (groupConditions?.length! > 0) {
          exportGroupedExcel(gridRef);
        } else {
          excelExportExtension?.exportToExcel();
        }
      }
    };

    const handleToggleProperties = () => {
      setShowPropertiesPanel((prev: boolean) => !prev);
      if (toolbarClickHandler) {
        toolbarClickHandler(TOOLBAR_PROPERTIES_ACTION);
      }
    };

    const getFormatterType = (column: ToolBarColumn) => {
      if (column.formatterType) {
        const registeredFormatter = getFormatterByType(column.formatterType);
        if (registeredFormatter && typeof registeredFormatter === "function") {
          return function (
            row: number,
            cell: number,
            value: unknown,
            columnDef: ToolBarColumn,
            dataContext: GridRowBase,
            grid?: GridLike,
          ) {
            // slickgrid guards every formatter call with `if (item)` (appendCellHtml / updateCell /
            // updateRow in slickGrid.js), so `dataContext` is always a row here - dummy rows
            // included, which is what this skips.
            if (dataContext.__isDummy) return "";
            try {
              // The registry describes this same slickgrid column with its own shape, whose
              // `_valueGetter` may also be a legacy string field path (FormatterColDef /
              // FormatterDateColDef in Formatters/Formatters.tsx); neither declaration subsumes
              // the other, so the column is restated rather than converted.
              const registryColumnDef = columnDef as Parameters<typeof registeredFormatter>[3];
              return registeredFormatter(row, cell, value, registryColumnDef, dataContext, grid);
            } catch (error) {
              console.error(`Formatter error for type "${column.formatterType}":`, error);
              // Stringified, not returned raw: slickgrid reads a non-primitive formatter result as a
              // `{ html, text }` descriptor (applyFormatResultToCellNode), so returning an object or
              // array value here blanked the cell instead of falling back to it.
              return String(value ?? "");
            }
          };
        }
      }
    };

    const renderToolbarItem = (item: ToolBarEntry, index: number) => {
      return (
        <div
          className={`flex items-center mr-2 ${
            item.disabled
              ? "opacity-40 cursor-not-allowed grayscale select-none pointer-events-auto *:pointer-events-none"
              : ""
          } ${item.hidden ? " hidden" : ""}`}
          key={index}
          onClick={item.template && !item.disabled ? () => handleClick(item.id) : undefined}
        >
          {!item.template && (
            <ButtonComponent
              // Every toolbar key is forwarded verbatim, `name` included: it reaches MUI as the DOM
              // button's `name` attribute, so dropping it would emit differently.
              {...item}
              key={item.id}
              sx={{ flexShrink: 0 }}
              className={`${index === 0 ? "ms-3" : ""} ${" " + item.cssClass} `}
              buttonType={item.buttonType || BUTTON_TYPES.BACKGROUND_BUTTON}
              onClick={() => {
                handleClick(item.id);
              }}
            >
              <div className="flex items-center">
                {(item.prefixIcon || item.prefixIconTemplate) && (
                  <div className="flex items-center me-1">
                    {item.prefixIcon && <IconComponent name={item.prefixIcon} width="1rem" height="1rem" />}
                    {item.prefixIconTemplate}
                  </div>
                )}
                <Text className="inline">{item.name}</Text>
                {(item.suffixIcon || item.suffixIconTemplate) && (
                  <div className="flex items-center ms-1">
                    {item.suffixIcon && <IconComponent name={item.suffixIcon} width="1.5rem" height="1.5rem" />}
                    {item.suffixIconTemplate}
                  </div>
                )}
              </div>
            </ButtonComponent>
          )}
          {item.template?.(item.props ? item.props : {})}
        </div>
      );
    };

    const openFilteringModal = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setModalState!("Filter");
    };
    const openGroupingModal = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setModalState!("Group");
    };
    const openSortingModal = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setModalState!("Sort");
    };

    const openRowResizeModal = (event: React.MouseEvent<HTMLElement>) => {
      setShowRowResize(true);
      setAnchorEl(event.currentTarget);
    };
    return (
      <>
        {(toolbar.length > 0 ||
          enableFilteringOption ||
          enableSortingOption ||
          enableGroupingOption ||
          enableColumnChooser ||
          enableRowResize ||
          excelExport ||
          enableViews ||
          enableRefreshOption ||
          enableSearchBar) && (
          <div
            className={`overflow-hidden flex gap-6 items-center bg-[#FAFCFF] ${
              showToolbarBorder && "border-b border-stroke_primary_light"
            } h-${height}`}
          >
            {showOverFlowBtns && (
              <div className="flex-shrink-0 flex items-center h-full cursor-pointer">
                <Button
                  sx={{
                    height: "100%",
                    minWidth: "2.25rem",
                    borderInlineEnd: "1px solid #D1D5DB",
                    borderRadius: "0.5rem 0 0 0.5rem",
                    color: "#6B7280",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#E5E7EB",
                    },
                  }}
                  onClick={() => handleArrowClick("backward")}
                  size="small"
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: "1.125rem" }} />
                </Button>
              </div>
            )}
            <div
              ref={toolRef}
              className="flex-grow flex items-center justify-between h-16 max-w-full gap-1 overflow-hidden"
            >
              <div className="flex  items-center">
                {enableViews && viewsTemplate()}
                {leftArray.map((item: ToolBarEntry, index: number) => {
                  if (item.tooltip) {
                    return (
                      <ToolTipComponent key={`start-${item.id}-${index}`} content={toolbarTooltipContent(item.tooltip)}>
                        {renderToolbarItem(item, index)}
                      </ToolTipComponent>
                    );
                  }
                  return renderToolbarItem(item, index);
                })}
                {excelExport && (
                  <ToolTipComponent content={t("Export to Excel")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={handleExportToExcel}
                      disabled={excelExportDisabled}
                    >
                      <div className="flex gap-1 items-center">
                        <IconComponent name="ExcelExportIcon" width="1rem" height="1rem" />
                        <Text className="inline">{t("Export")}</Text>
                      </div>
                    </ButtonComponent>
                  </ToolTipComponent>
                )}
              </div>

              <div className="flex items-center">
                {rightArray.map((item: ToolBarEntry, index: number) => {
                  if (item.tooltip) {
                    return (
                      <ToolTipComponent
                        key={`end-scrollable-${item.id}-${index}`}
                        content={toolbarTooltipContent(item.tooltip)}
                      >
                        {renderToolbarItem(item, index)}
                      </ToolTipComponent>
                    );
                  }
                  return renderToolbarItem(item, index);
                })}
              </div>
            </div>

            <div className="shrink-0 flex items-center">
              {leftStickyArray.map((item: ToolBarEntry, index: number) => {
                if (item.tooltip) {
                  return (
                    <ToolTipComponent
                      key={`end-scrollable-${item.id}-${index}`}
                      content={toolbarTooltipContent(item.tooltip)}
                    >
                      {renderToolbarItem(item, index)}
                    </ToolTipComponent>
                  );
                }
                return renderToolbarItem(item, index);
              })}
              {enableSearchBar && (
                <GridSearchInput
                  value={searchTerm}
                  placeholder={searchPlaceholder}
                  onSearchChange={onSearchChange}
                  debounceMs={searchDebounceMs}
                />
              )}

              {enableRefreshOption && (
                <div id={gridId ? `refresh-icon-${gridId}` : "refresh-icon"}>
                  <ToolTipComponent content={t("Refresh")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      iconButton={true}
                      data-testid="gridRefreshOption"
                      // Caller owns refresh via refreshOptionCallBack; fall back to the grid's own
                      // data reload when no callback is provided.
                      onClick={() => (refreshOptionCallBack ? refreshOptionCallBack() : gridRef.current?.refresh?.())}
                    >
                      <IconComponent name="refresh" width="1.5rem" height="1.5rem" />
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}

              {enableGroupingOption && (
                <div id={gridId ? `grouping-icon-${gridId}` : "grouping-icon"}>
                  <ToolTipComponent content={t("Grouping")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={openGroupingModal}
                      iconButton={true}
                    >
                      <IconComponent name="Grouping" width="1.5rem" height="1.5rem" />

                      {groupConditions?.length! > 0 && (
                        <div
                          aria-label="Active sort conditions"
                          style={{
                            position: "absolute",
                            top: 0,
                            insetInlineEnd: 0,
                            width: "0.5rem",
                            height: "0.5rem",
                            borderRadius: "6.25rem",
                            background: "var(--Notification-notification-primary, #6BBBE3)",
                            transform: "translate(-50%, 50%)",
                            zIndex: 10,
                          }}
                        />
                      )}
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}
              {enableFilteringOption && (
                <div id={gridId ? `filtering-icon-${gridId}` : "filtering-icon"}>
                  <ToolTipComponent content={t("Filter")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={openFilteringModal}
                      iconButton={true}
                    >
                      <IconComponent name="Filter" width="1.5rem" height="1.5rem" />
                      {filterConditions?.length! > 0 && (
                        <div
                          aria-label="Active sort conditions"
                          style={{
                            position: "absolute",
                            top: 0,
                            insetInlineEnd: 0,

                            width: "0.5rem",
                            height: "0.5rem",

                            borderRadius: "6.25rem",
                            background: "var(--Notification-notification-primary, #6BBBE3)",

                            transform: "translate(-50%, 50%)",

                            zIndex: 10,
                          }}
                        />
                      )}
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}
              {enableSortingOption && (
                <div id={gridId ? `sorting-icon-${gridId}` : "sorting-icon"}>
                  <ToolTipComponent content={t("Sort")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={openSortingModal}
                      iconButton={true}
                    >
                      <IconComponent name="Sort" width="1.5rem" height="1.5rem" />

                      {sortConditions?.length! > 0 && (
                        <div
                          aria-label="Active sort conditions"
                          style={{
                            position: "absolute",
                            top: 0,
                            insetInlineEnd: 0,

                            width: "0.5rem",
                            height: "0.5rem",

                            borderRadius: "6.25rem",
                            background: "var(--Notification-notification-primary, #6BBBE3)",

                            transform: "translate(-50%, 50%)",

                            zIndex: 10,
                          }}
                        />
                      )}
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}

              {(enableGroupingOption || enableFilteringOption || enableSortingOption) &&
                (enableColumnChooser || propertiesPanel) && <div className="w-px h-8 bg-[#666A84] mx-1" />}

              {enableRowResize && (
                <div id="row-resize-icon">
                  <ToolTipComponent content={t("Row Resize")}>
                    <ButtonComponent
                      className="me-2 ms-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={openRowResizeModal}
                      iconButton={true}
                    >
                      {getRowIcon(currentRowSize)}
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}

              {enableColumnChooser && (
                <ToolTipComponent content={t("Column Chooser")}>
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.TEXT_BUTTON}
                    className="me-2"
                    iconButton={true}
                    onClick={() => setShowColumnChooser(true)}
                  >
                    <IconComponent name="Column_Chooser" width="1.5rem" height="1.5rem" />
                  </ButtonComponent>
                </ToolTipComponent>
              )}

              {propertiesPanel && (
                <ToolTipComponent content={t("Properties Panel")}>
                  <ButtonComponent
                    className="me-2"
                    buttonType={BUTTON_TYPES.TEXT_BUTTON}
                    iconButton={true}
                    disabled={propertiesDisabled}
                    onClick={handleToggleProperties}
                  >
                    <IconComponent
                      name={showPropertiesPanel ? "PropertiesPanelOpen" : "PropertiesPanelClosed"}
                      width="1.5rem"
                      height="1.5rem"
                    />
                  </ButtonComponent>
                </ToolTipComponent>
              )}
              {rightStickyArray.map((item: ToolBarEntry, index: number) => {
                if (item.tooltip) {
                  return (
                    <ToolTipComponent
                      key={`end-scrollable-${item.id}-${index}`}
                      content={toolbarTooltipContent(item.tooltip)}
                    >
                      {renderToolbarItem(item, index)}
                    </ToolTipComponent>
                  );
                }
                return renderToolbarItem(item, index);
              })}
            </div>

            {showOverFlowBtns && (
              <div className="shrink-0 flex items-center h-full cursor-pointer">
                <Button
                  sx={{
                    height: "100%",
                    minWidth: "2.25rem",
                    borderInlineStart: "1px solid #D1D5DB",
                    borderRadius: "0 0.5rem 0.5rem 0",
                    color: "#6B7280",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#E5E7EB",
                    },
                  }}
                  onClick={() => handleArrowClick("forward")}
                  size="small"
                >
                  <ArrowForwardIosIcon sx={{ fontSize: "1.125rem" }} />
                </Button>
              </div>
            )}
          </div>
        )}
        {showColumnChooser && (
          <GridColumnChooser
            ref={gridRef as React.Ref<SlickGrid1Ref<GridRowBase>>}
            onSave={(cols: string[]) => {
              if (cols.length === 0) {
                alert(t("Please select at least one column"));
                return;
              }
              setShowColumnChooser(false);
              const slickGrid = gridRef.current?.slickGrid;
              if (!slickGrid) return;
              const existingColsWidth = slickGrid
                .getColumns()
                .reduce((widths: Record<string, number | undefined>, col: ToolBarColumn) => {
                  widths = {
                    ...widths,
                    [col.id]: col.width,
                  };
                  return widths;
                }, {});

              const clipModeFormatter = (
                row: number,
                cell: number,
                value: unknown,
                columnDef: ToolBarColumn,
                dataContext: GridRowBase,
              ) => {
                // See getFormatterType: slickgrid never invokes a formatter without a row.
                if (dataContext.__isDummy) return "";

                let resolvedValue = value;
                if (resolvedValue === undefined && columnDef.field) {
                  resolvedValue = get(dataContext, columnDef.field);
                }
                const finalValue = columnDef._valueGetter ? columnDef._valueGetter(dataContext) : resolvedValue;

                if (stateRef!.current!.enableWrapText) {
                  const container = document.createElement("div");
                  container.style.whiteSpace = "pre-wrap";
                  container.style.wordBreak = "break-word";
                  container.style.overflow = "hidden";
                  // textContent stringifies whatever it is given; doing it here keeps that explicit.
                  container.textContent = String(finalValue ?? "");
                  return container;
                }
                return ellipseFormatterWithTooltip!({
                  value: finalValue,
                  column: columnDef,
                });
              };
              const buttoncssClass = "mdi mdi-dots-vertical text-slate-900 inline-block  cursor-pointer mt-0";

              const headerConfig = {
                buttons: [
                  {
                    cssClass: buttoncssClass,
                    command: "toggle-highlight",
                  },
                ],
              };

              const allColDefs = gridRef.current!.gridService.getAllColumnDefinitions();
              const excludedCols = allColDefs.filter(
                (c: ToolBarColumn) => c.excludeFromColumnPicker && !specialColumns!.some((sc) => sc.id === c.id),
              );

              const finalCols = [
                ...specialColumns!,
                ...excludedCols,
                // flatMap rather than map+filter(Boolean): a chosen id with no matching definition
                // contributes nothing, and the result keeps a column element type throughout.
                ...cols.flatMap((colId: string) => {
                  const col: ToolBarColumn | undefined = allColDefs.find((c: ToolBarColumn) => c.id === colId);
                  if (!col) return [];
                  return [
                    {
                      ...col,
                      excelExportOptions: {
                        valueParserCallback: (dataObj: unknown, { dataContext }: { dataContext: GridRowBase }) =>
                          col._valueGetter ? col._valueGetter(dataContext) : _.get(dataContext, col.field),
                      },
                      width: existingColsWidth[colId] || col.width,
                      formatter: col.formatterType
                        ? getFormatterType(col)
                        : col.clipMode || (!col.formatter && !col.formatterType)
                          ? clipModeFormatter
                          : col.formatter,
                      header: headerConfig,
                      sortable: true,
                      groupable: true,
                      filterable: true,
                      // `as const`: slickgrid's `Column.type` is a literal union, not a bare string.
                      type: "string" as const,
                      headerCssClass: (col.headerCssClass || "") + " custom-header-column",
                      initiallyHidden: false,
                    },
                  ];
                }),
              ];
              slickGrid.setColumns(finalCols);
              onSaveColumns?.(finalCols);
              slickGrid.invalidate();
              slickGrid.render();
            }}
            onCancel={() => setShowColumnChooser(false)}
          />
        )}
        {/*
          MODAL BOUNDARY. FilterModal and SortGroupModal each re-declare the grid's filter / sort /
          group model with their own structural copies (FilterModal.tsx:42-53 + 94-139,
          SortGroupModal.tsx:24-60). Those describe the very objects SlickGrid1 already holds, but
          every member disagrees with the grid's declaration in a way no runtime check can bridge:
          `FilterCondition.value` is a closed union there and `unknown` on the grid's
          `AppliedFilterCondition`; `remoteDataSource` is `string | null` there but may also be the
          fetcher function on the grid; `Condition.direction` is required there and optional on
          `GroupCondition`; `ColumnDefinition.field` is a required `string` there and `keyof TRow`
          on the grid side. This toolbar only forwards these values - it neither reads nor produces
          them - so reconciling the two declarations means editing the modals (or the grid), not
          this file. Each crossing below carries its own disable naming the two declarations that
          disagree; nothing outside those attribute lines is silenced.
        */}
        {modalState === "Filter" && (
          <FilterModal
            isOpen
            onClose={() => {
              setTempCondition!(null);
              setModalState!("");
            }}
            columnDefinitions={
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: FilterModal declares `ColumnDefinition.field` as a required `string` (FilterModal.tsx:94-104); the grid's is `keyof TRow`
              (() => {
                const visibleColIds = new Set<PropertyKey | undefined>(
                  gridRef.current?.slickGrid.getColumns().map((c: ToolBarColumn) => c.id || c.field) || [],
                );
                return columnDefinitions!.filter((col: ToolBarColumnDefinition) => {
                  const colId = col.id || col.field;

                  return visibleColIds.size === 0 || visibleColIds.has(colId);
                });
              })() as FilterModalProps["columnDefinitions"]
            }
            // The modal's condition rows brand `value` narrower than the toolbar's applied-filter
            // rows do (`unknown`), so the pair has to be asserted across as a unit to keep the
            // state setter usable in both directions.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: `FilterCondition.value` is a closed union in FilterModal (`FilterConditionValue`, FilterModal.tsx:42-53), `unknown` on the grid's `AppliedFilterCondition`
            conditions={filterConditions as unknown[] & FilterModalProps["conditions"]}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: setter half of the `conditions` pair above - same `FilterCondition` disagreement
            setConditions={setFilterConditions as FilterModalProps["setConditions"]}
            anchorEl={anchorEl ?? modalAnchorEl ?? null}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: FilterModal declares `remoteDataSource: string | null` (FilterModal.tsx:115); the grid may also hand it the fetcher function (index.tsx:620)
            remoteDataSource={remoteDataSource as FilterModalProps["remoteDataSource"]}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: FilterModal declares `beforeDataBound: (data: unknown) => unknown[]` (FilterModal.tsx:117); the grid's is row-typed (index.tsx:619)
            beforeDataBound={beforeDataBound as FilterModalProps["beforeDataBound"]}
            datasetForFetchingOptions={datasetForFetchingOptions!}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: FilterModal declares `query: FilterQuery | null` (FilterModal.tsx:121); the grid's query shape is its own `QueryShape` (index.tsx:611)
            query={query as FilterModalProps["query"]}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: FilterModal declares `callRemote` as resolving to its own `ApiResponse` (FilterModal.tsx:110); the grid resolves `RemoteDataResponse<T>` (index.tsx:2203-2206)
            callRemote={callRemote as FilterModalProps["callRemote"]}
            localData={!remoteDataSource}
            getUpdatedFilterConditions={getUpdatedFilterConditions!}
            applyFilterFunction={applyFilterFunction!}
            gridRef={gridRef}
            setAppliedFilters={setAppliedFilters!}
            appliedFilters={appliedFilters!}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: setter typed against FilterModal's own `FilterPredicate` (FilterModal.tsx:133)
            setFilterPredicates={setFilterPredicates as FilterModalProps["setFilterPredicates"]}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: setter typed against FilterModal's own `FilterConditionEntry` (FilterModal.tsx:135)
            setFilterConditionsObject={setFilterConditionsObject as FilterModalProps["setFilterConditionsObject"]}
            currentColumnId={currentColumnId}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: same `FilterCondition` disagreement as `conditions` above (FilterModal.tsx:136-137)
            setTempCondition={setTempCondition as FilterModalProps["setTempCondition"]}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: same `FilterCondition` disagreement as `conditions` above (FilterModal.tsx:136-137)
            tempCondition={tempCondition as FilterModalProps["tempCondition"]}
          />
        )}
        {modalState === "Group" && (
          <SortGroupModal
            isOpen
            anchorEl={anchorEl ?? modalAnchorEl ?? null}
            onClose={() => setModalState!("")}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: SortGroupModal declares `ColumnDefinition.field` as a required `string` (SortGroupModal.tsx:30-38); the grid's is `keyof TRow`
            columnDefinitions={columnDefinitions as SortGroupModalProps["columnDefinitions"]}
            // The modal requires a direction on every row; a group condition may omit one.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: SortGroupModal requires `Condition.direction` on every row (SortGroupModal.tsx:24-28); it is optional on the grid's `GroupCondition`
            conditions={groupConditions as unknown[] & SortGroupModalProps["conditions"]}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: setter half of the `conditions` pair above - same `direction` disagreement
            setConditions={setGroupConditions as SortGroupModalProps["setConditions"]}
            toggleGrouping={toggleGrouping}
            title="Group"
          />
        )}
        {showRowResize && (
          <RowResizeModal
            gridRef={gridRef}
            anchorEl={anchorEl}
            handleRowResizeOptionClick={handleRowResizeOptionClick}
            setEnableWrapText={setEnableWrapText}
            currentRowSize={currentRowSize}
            groupConditions={groupConditions}
            enableWrapText={enableWrapText}
            setCurrentRowSize={setCurrentRowSize}
            onClose={() => {
              setShowRowResize(false);
              setAnchorEl(null);
            }}
          />
        )}

        {modalState === "Sort" && (
          <SortGroupModal
            isOpen
            anchorEl={anchorEl ?? modalAnchorEl ?? null}
            onClose={() => setModalState!("")}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- MODAL BOUNDARY: SortGroupModal declares `ColumnDefinition.field` as a required `string` (SortGroupModal.tsx:30-38); the grid's is `keyof TRow`
            columnDefinitions={columnDefinitions as SortGroupModalProps["columnDefinitions"]}
            conditions={sortConditions!}
            setConditions={setSortConditions!}
            title="Sort"
          />
        )}
      </>
    );
  },
);
ToolBar.displayName = "ToolBar";

export default ToolBar;
